import './AssetManagerVerification.css';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { errorMessages } from '~/constants/errorMessages.js';
import OTPCodeModal from '~/components/OTPCodeModal/OTPCodeModal';
import { useAppDispatch } from '~/store/appDispatch';
import { clearUsersState, setUserByUrlToken } from '~/store/users/slices';
import { ClientRole } from '~/constants/clientTypes';
import {
  getFormDataAssetManagers,
  updateDataProcessingAcceptedAssetManagers,
} from '~/store/identification-form/thunks/assetManagersThunk';
import { TGeneralInfoAssetManager } from '../../forms/GeneralInfo/types';
import { useNavigate } from 'react-router';
import { AxiosError } from 'axios';
import { Box, Typography } from '@mui/material';
import { styles as assetManager } from './styles.ts';
import { informationAboutLegislation } from '~/modules/identification/constants.ts';
import { selectToken, selectTokenData, selectUserError } from '~/store/users/selectors.ts';
import { setIdentificationError } from '~/store/identification-form/slices.ts';

interface TAssetManagerVerificationProps {
  onSuccess: () => void;
  offLoading: () => void;
}

export default function AssetManagerVerification({
  onSuccess,
  offLoading,
}: TAssetManagerVerificationProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userError = useSelector(selectUserError);
  const token = useSelector(selectToken);
  const { phone, managerId, appId } = useSelector(selectTokenData) || {};

  const [modalOTPVerification, setModalOTPVerification] = useState(false);
  const [error, setError] = useState('');
  const [consentToDataProcessing, setConsentToDataProcessing] = useState(true);

  useEffect(() => {
    dispatch(setUserByUrlToken(ClientRole.ASSET_MANAGER));
  }, [dispatch]);

  const setIdentError = (message: string) => {
    dispatch(
      setIdentificationError({
        errorMessage: message,
        discard: () => {
          navigate('/services');
          dispatch(clearUsersState());
        },
      })
    );
  };

  const acceptConsentDataProcessing = () => {
    updateIsDataProcessingAccepted(true);
    setModalOTPVerification(true);
  };

  const updateIsDataProcessingAccepted = (isDataProcessingAccepted: boolean) => {
    dispatch(
      updateDataProcessingAcceptedAssetManagers({
        managerId,
        applicationId: appId,
        isDataProcessingAccepted,
      })
    );
  };

  useEffect(() => {
    (async () => {
      if (managerId && appId) {
        try {
          const data = await dispatch(
            getFormDataAssetManagers({ managerId, applicationId: appId })
          ).unwrap();
          let isDataProcessingAccepted =
            (data?.generalInfo as TGeneralInfoAssetManager)?.isDataProcessingAccepted === true;
          setConsentToDataProcessing(isDataProcessingAccepted);

          offLoading();

          if (isDataProcessingAccepted) {
            setModalOTPVerification(true);
          }
        } catch (error) {
          offLoading();
          const axiosError = error as AxiosError;
          if ([401].includes(axiosError.status!)) {
            navigate('/identification/asset-managers/questionnaire/expired', { replace: true });
          } else {
            setIdentError(errorMessages.SERVICE_UNAVAILABLE);
          }
        }
      }
    })();
  }, [appId, managerId]);

  useEffect(() => {
    if (userError) {
      setIdentError(errorMessages.SERVICE_UNAVAILABLE);
    }
  }, [userError]);

  return (
    <>
      {!consentToDataProcessing && (
        <Box sx={assetManager.assetManagerContainer}>
          <Box sx={assetManager.assetManager}>
            <Typography variant={'h5'} sx={assetManager.headerText}>
              Згода на обробку даних
            </Typography>
            <Box sx={assetManager.formContainer}>
              {informationAboutLegislation.map((text, i) => (
                <Box key={i} sx={assetManager.container}>
                  <Box sx={assetManager.containerText}></Box>
                  <Typography variant="body1" sx={{ lineHeight: 1.65 }}>
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={assetManager.buttonBlock}>
              <Button variant="contained" size="large" onClick={acceptConsentDataProcessing}>
                Підтверджую
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <OTPCodeModal
        modalOTPVerification={modalOTPVerification}
        phone={phone}
        token={token}
        error={error}
        setError={setError}
        onValidationSuccess={onSuccess}
      />
    </>
  );
}
